import { type ExplodeFlow, NodeRole } from "../../types";
import { ASSET_REPORT_TYPES } from "../assets/assetReportTypes";
import type { ChainStep } from "./buildAssetGraph";
import { titleEdge, valueEdge } from "./graphEdges";
import { COLUMN_GAP, GRID_TOP, NODE_WIDTH, OVERLAY_Z, ROOT_Y } from "./graphLayout";
import { graphNode } from "./graphNode";

export interface AssetRecord {
    id: string;
    label: string;
    counts: Record<string, number>;
}

const ASSET_ID = "asset-focus";

export const buildAssetDocsGraph = ({
    asset,
    chain,
}: {
    asset: AssetRecord;
    chain: ChainStep[];
}): ExplodeFlow => {
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rowWidth = ASSET_REPORT_TYPES.length * stepX - COLUMN_GAP;
    const assetX = (rowWidth - NODE_WIDTH) / 2;

    const chainSteps = [...chain, { label: asset.label }];
    const chainNodes = chainSteps.map((step, index) => {
        const isAsset = index === chainSteps.length - 1;
        return graphNode(
            isAsset ? ASSET_ID : `chain-${index}`,
            assetX - (chainSteps.length - 1 - index) * stepX,
            ROOT_Y,
            {
                label: step.label,
                caption: step.caption,
                role: index === 0 ? NodeRole.ROOT : NodeRole.GROUP,
                focused: isAsset,
                assetId: isAsset ? asset.id : undefined,
                selection: [],
            },
            OVERLAY_Z,
        );
    });

    const chainEdges = chainNodes
        .slice(1)
        .map((step, index) => titleEdge(chainNodes[index].id, step.id, `e-chain-${index}`));

    const reportNodes = ASSET_REPORT_TYPES.map((report, index) =>
        graphNode(
            `rpt-${index}`,
            index * stepX,
            GRID_TOP,
            {
                label: report.label,
                count: asset.counts[report.countField] ?? 0,
                role: NodeRole.GROUP,
                selection: [],
                reportTarget: {
                    datasourceId: report.datasourceId,
                    filterKey: report.filterKey,
                    filterValue: asset.id,
                    label: report.label,
                },
            },
            OVERLAY_Z,
        ),
    );

    const reportEdges = reportNodes.map((reportNode, index) =>
        valueEdge(ASSET_ID, reportNode.id, `e-rpt-${index}`),
    );

    return {
        nodes: [...chainNodes, ...reportNodes],
        edges: [...chainEdges, ...reportEdges],
    };
};
